import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// A4 portrait — width 210mm, height 297mm
// 0.5 inch margins on every side = 12.7mm
const A4_W = 210;
const A4_H = 297;
const MARGIN = 12.7;

/**
 * Render a DOM node into a multi-page A4 PDF with 0.5" margins on every side.
 * The captured snapshot scales to the available page width and paginates
 * vertically across as many pages as needed.
 *
 * @param {HTMLElement} node    The DOM node to render
 * @param {string}      filename Filename to save (must end with .pdf)
 * @param {Object}      [opts]
 * @param {"p"|"l"}     [opts.orientation="p"]  "p" portrait or "l" landscape
 */
export async function exportNodeToPDF(node, filename, opts = {}) {
  if (!node) throw new Error("exportNodeToPDF: node is required");
  const orientation = opts.orientation === "l" || opts.orientation === "landscape" ? "l" : "p";

  // Let webfonts settle so html2canvas captures the final layout
  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (_) { /* ignore */ }
  }

  // Reveal elements that exist only for the PDF (data-pdf-only="true")
  const pdfOnly = Array.from(node.querySelectorAll('[data-pdf-only="true"]'));
  const prevDisplay = pdfOnly.map((el) => el.style.display);
  pdfOnly.forEach((el) => { el.style.display = "block"; });
  // Hide elements that should NOT appear in the PDF (data-pdf-hide="true")
  const pdfHide = Array.from(node.querySelectorAll('[data-pdf-hide="true"]'));
  const prevHideDisplay = pdfHide.map((el) => el.style.display);
  pdfHide.forEach((el) => { el.style.display = "none"; });

  let canvas;
  try {
    canvas = await html2canvas(node, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      windowWidth: node.scrollWidth,
      windowHeight: node.scrollHeight,
    });
  } finally {
    // Restore visibility
    pdfOnly.forEach((el, i) => { el.style.display = prevDisplay[i]; });
    pdfHide.forEach((el, i) => { el.style.display = prevHideDisplay[i]; });
  }

  const imgData = canvas.toDataURL("image/png");
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation });

  // Swap page dimensions for landscape
  const pageW = orientation === "l" ? A4_H : A4_W;
  const pageH = orientation === "l" ? A4_W : A4_H;
  const usableW = pageW - 2 * MARGIN;
  const usableH = pageH - 2 * MARGIN;
  const imgHeightMm = (canvas.height * usableW) / canvas.width;

  // Single-page fast path
  if (imgHeightMm <= usableH) {
    doc.addImage(imgData, "PNG", MARGIN, MARGIN, usableW, imgHeightMm);
    doc.save(filename);
    return;
  }

  // Multi-page: draw the full image and shift it up by one page-height on each new page.
  // jsPDF/PDF viewers clip the image to the page boundary, so only the slice belonging
  // to that page is visible.
  let yOffset = MARGIN;
  let heightLeft = imgHeightMm;
  doc.addImage(imgData, "PNG", MARGIN, yOffset, usableW, imgHeightMm);
  heightLeft -= usableH;
  while (heightLeft > 0) {
    yOffset -= usableH;
    doc.addPage();
    doc.addImage(imgData, "PNG", MARGIN, yOffset, usableW, imgHeightMm);
    heightLeft -= usableH;
  }
  doc.save(filename);
}

/**
 * Build a filesystem-safe slug from a business name.
 */
export function bizSlug(name) {
  return String(name || "ledger")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "ledger";
}

/**
 * Render a sequence of DOM nodes into a multi-page A4 PDF — one node per page.
 *
 * Each node is captured to its own page, scaled to a configurable fraction of
 * the usable page width (default 80%) and centered horizontally. Tall nodes
 * paginate vertically across additional pages. Nodes (or descendants) marked
 * with data-pdf-hide="true" are temporarily hidden during capture; nodes
 * marked data-pdf-only="true" are temporarily shown.
 *
 * @param {HTMLElement[]} nodes   Ordered list of DOM nodes — one per starting page
 * @param {string}        filename
 * @param {Object}        [opts]
 * @param {"p"|"l"}       [opts.orientation="p"]  Page orientation
 * @param {number}        [opts.scale=0.8]        Fraction of usable width to use (0–1)
 * @param {HTMLElement}   [opts.headerNode]       Branded header rendered at the top of EVERY page
 */
export async function exportSectionsToPDF(nodes, filename, opts = {}) {
  const cleanNodes = (nodes || []).filter(Boolean);
  if (cleanNodes.length === 0) throw new Error("exportSectionsToPDF: nodes required");
  const orientation = opts.orientation === "l" || opts.orientation === "landscape" ? "l" : "p";
  const widthScale = Math.min(1, Math.max(0.2, Number(opts.scale ?? 0.8)));

  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (_) { /* ignore */ }
  }

  const doc = new jsPDF({ unit: "mm", format: "a4", orientation });
  const pageW = orientation === "l" ? A4_H : A4_W;
  const pageH = orientation === "l" ? A4_W : A4_H;
  const usableW = pageW - 2 * MARGIN;
  const usableH = pageH - 2 * MARGIN;
  const drawW = usableW * widthScale;
  const xOffset = MARGIN + (usableW - drawW) / 2; // center horizontally

  // Capture a single node to an image and return {imgData, heightMm} at the given target width.
  const captureNode = async (node, targetWidthMm) => {
    const pdfOnly = Array.from(node.querySelectorAll('[data-pdf-only="true"]'));
    const prevOnly = pdfOnly.map((el) => el.style.display);
    pdfOnly.forEach((el) => { el.style.display = "block"; });
    const pdfHide = Array.from(node.querySelectorAll('[data-pdf-hide="true"]'));
    const prevHide = pdfHide.map((el) => el.style.display);
    pdfHide.forEach((el) => { el.style.display = "none"; });

    let canvas;
    try {
      canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        windowWidth: node.scrollWidth,
        windowHeight: node.scrollHeight,
      });
    } finally {
      pdfOnly.forEach((el, i) => { el.style.display = prevOnly[i]; });
      pdfHide.forEach((el, i) => { el.style.display = prevHide[i]; });
    }
    return { imgData: canvas.toDataURL("image/png"), heightMm: (canvas.height * targetWidthMm) / canvas.width };
  };

  // Pre-capture the branded header once at full usable width — reused on every page.
  let header = null;
  if (opts.headerNode) {
    header = await captureNode(opts.headerNode, usableW);
  }

  const drawHeader = () => {
    if (!header) return MARGIN;
    doc.addImage(header.imgData, "PNG", MARGIN, MARGIN, usableW, header.heightMm);
    return MARGIN + header.heightMm + 6; // top of content area
  };

  for (let i = 0; i < cleanNodes.length; i++) {
    if (i > 0) doc.addPage();
    const contentTop = drawHeader();
    const remainOnPage = pageH - contentTop - MARGIN;

    const node = cleanNodes[i];
    const { imgData, heightMm } = await captureNode(node, drawW);

    if (heightMm <= remainOnPage) {
      doc.addImage(imgData, "PNG", xOffset, contentTop, drawW, heightMm);
      continue;
    }
    // Multi-page node: place full image with negative y-offsets so each
    // additional page renders the next vertical slice (clipped by the page).
    let yOffset = contentTop;
    let heightLeft = heightMm;
    doc.addImage(imgData, "PNG", xOffset, yOffset, drawW, heightMm);
    heightLeft -= remainOnPage;
    while (heightLeft > 0) {
      doc.addPage();
      const nextTop = drawHeader();
      yOffset = nextTop - (heightMm - heightLeft);
      doc.addImage(imgData, "PNG", xOffset, yOffset, drawW, heightMm);
      heightLeft -= (pageH - nextTop - MARGIN);
    }
  }

  doc.save(filename);
}
